/**
 * AiMarkdown — renders the markdown subset that AI responses actually emit:
 *   • **bold** and *italic* inline spans
 *   • Numbered lists (1. item)
 *   • Bulleted lists (-, *, •)
 *   • Headings (## Heading)
 *   • Blank-line-separated paragraphs
 *
 * User messages are NOT passed through this component; they stay as plain text.
 * The renderer never uses dangerouslySetInnerHTML — it builds a React tree so
 * there is no XSS risk even if the AI content contained HTML tags.
 */

import React from "react";

// ---------------------------------------------------------------------------
// Inline parser: bold / italic
// ---------------------------------------------------------------------------
type InlineNode = { type: "text"; value: string } | { type: "bold"; value: string } | { type: "italic"; value: string };

function parseInline(raw: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  // Patterns: **bold**, *italic*, _italic_
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_)/gs;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) nodes.push({ type: "text", value: raw.slice(last, m.index) });
    if (m[0].startsWith("**")) {
      nodes.push({ type: "bold", value: m[2] });
    } else {
      nodes.push({ type: "italic", value: m[3] ?? m[4] });
    }
    last = re.lastIndex;
  }
  if (last < raw.length) nodes.push({ type: "text", value: raw.slice(last) });
  return nodes;
}

function renderInline(raw: string, key: React.Key): React.ReactNode {
  const nodes = parseInline(raw);
  if (nodes.length === 1 && nodes[0].type === "text") return nodes[0].value;
  return (
    <React.Fragment key={key}>
      {nodes.map((n, i) => {
        if (n.type === "bold") return <strong key={i}>{n.value}</strong>;
        if (n.type === "italic") return <em key={i}>{n.value}</em>;
        return <React.Fragment key={i}>{n.value}</React.Fragment>;
      })}
    </React.Fragment>
  );
}

// ---------------------------------------------------------------------------
// Block parser
// ---------------------------------------------------------------------------
type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "bullet"; items: string[] }
  | { type: "ordered"; items: string[] }
  | { type: "paragraph"; text: string };

const ORDERED_ITEM = /^\s*\d+[.)]\s+(.*)$/;

function looksLikeOrderedListContinuation(
  line: string,
  previousText: string,
  separatedByBlank: boolean,
): boolean {
  if (
    !line.trim() ||
    ORDERED_ITEM.test(line) ||
    /^\s*[-*•]\s+/.test(line) ||
    /^\s*#{1,6}\s+/.test(line)
  ) {
    return false;
  }
  if (/^\s+\S/.test(line)) return true;
  return (
    !separatedByBlank &&
    previousText.length > 0 &&
    !/[.!?。！？…]$/.test(previousText) &&
    /^\p{Ll}/u.test(line.trim())
  );
}

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.split("\n");
  const blocks: Block[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines between blocks
    if (line.trim() === "") { i++; continue; }

    // Heading: ## text
    const headingMatch = line.match(/^(#{1,6})\s+(.*)/);
    if (headingMatch) {
      blocks.push({ type: "heading", level: headingMatch[1].length, text: headingMatch[2] });
      i++;
      continue;
    }

    // Ordered list: "1. text", "1) text", …
    const orderedItem = line.match(ORDERED_ITEM);
    if (orderedItem) {
      const items: string[] = [];
      let previousText = orderedItem[1].trim();
      let separatedByBlank = false;
      items.push(previousText);
      i++;

      while (i < lines.length) {
        const nextItem = lines[i].match(ORDERED_ITEM);
        if (nextItem) {
          items.push(nextItem[1].trim());
          previousText = nextItem[1].trim();
          separatedByBlank = false;
          i++;
          continue;
        }

        if (lines[i].trim() === "") {
          let nextNonBlank = i;
          while (nextNonBlank < lines.length && lines[nextNonBlank].trim() === "") {
            nextNonBlank++;
          }
          if (nextNonBlank < lines.length && ORDERED_ITEM.test(lines[nextNonBlank])) {
            i = nextNonBlank;
            separatedByBlank = true;
            continue;
          }
          if (
            nextNonBlank < lines.length &&
            looksLikeOrderedListContinuation(
              lines[nextNonBlank],
              previousText,
              true,
            )
          ) {
            items[items.length - 1] += ` ${lines[nextNonBlank].trim()}`;
            previousText = lines[nextNonBlank].trim();
            i = nextNonBlank + 1;
            separatedByBlank = false;
            continue;
          }
          break;
        }

        if (
          looksLikeOrderedListContinuation(
            lines[i],
            previousText,
            separatedByBlank,
          )
        ) {
          items[items.length - 1] += ` ${lines[i].trim()}`;
          previousText = lines[i].trim();
          separatedByBlank = false;
          i++;
          continue;
        }
        break;
      }
      blocks.push({ type: "ordered", items });
      continue;
    }

    // Bullet list: "- text", "* text", "• text"
    if (/^[-*•]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*•]\s+/, ""));
        i++;
      }
      blocks.push({ type: "bullet", items });
      continue;
    }

    // Paragraph — collect consecutive non-empty, non-list, non-heading lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,6})\s/.test(lines[i]) &&
      !ORDERED_ITEM.test(lines[i]) &&
      !/^[-*•]\s/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: "paragraph", text: paraLines.join(" ") });
    }
  }

  return blocks;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface AiMarkdownProps {
  content: string;
  className?: string;
}

export function AiMarkdown({ content, className }: AiMarkdownProps) {
  const blocks = parseBlocks(content);

  const rendered = blocks.map((block, bi) => {
    switch (block.type) {
      case "heading": {
        const text = renderInline(block.text, bi);
        const cls = "font-semibold mt-1 mb-0.5";
        if (block.level <= 2) return <h2 key={bi} className={cls}>{text}</h2>;
        if (block.level === 3) return <h3 key={bi} className={cls}>{text}</h3>;
        return <h4 key={bi} className={cls}>{text}</h4>;
      }
      case "ordered":
        return (
          <ol key={bi} className="list-decimal list-inside space-y-0.5 my-1 pl-1">
            {block.items.map((item, ii) => (
              <li key={ii}>{renderInline(item, ii)}</li>
            ))}
          </ol>
        );
      case "bullet":
        return (
          <ul key={bi} className="list-disc list-inside space-y-0.5 my-1 pl-1">
            {block.items.map((item, ii) => (
              <li key={ii}>{renderInline(item, ii)}</li>
            ))}
          </ul>
        );
      case "paragraph":
      default:
        return (
          <p key={bi} className={bi > 0 ? "mt-1.5" : undefined}>
            {renderInline(block.text, bi)}
          </p>
        );
    }
  });

  return (
    <div className={className}>
      {rendered}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utility: strip markdown symbols for plain-text TTS
// ---------------------------------------------------------------------------
export function stripMarkdown(text: string): string {
  return text
    // headings
    .replace(/^#{1,6}\s+/gm, "")
    // bold / italic
    .replace(/\*\*(.+?)\*\*/gs, "$1")
    .replace(/\*(.+?)\*/gs, "$1")
    .replace(/_(.+?)_/gs, "$1")
    // list markers
    .replace(/^[-*•]\s+/gm, "")
    .replace(/^\s*\d+[.)]\s+/gm, "")
    // collapse multiple blank lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
